import React, { useState } from 'react';
import { Menu, Bell, Plus, Heart, Search, Home, Building2, Flame, Bot, User } from 'lucide-react';
import Sidebar from './Sidebar';
import RightPanel from './RightPanel';
import UserAuthMenu from '../UserAuthMenu';
import { Link, useLocation, NavLink } from 'react-router-dom';

const TOPBAR_HIDE_PATHS = ['/admin', '/affiliate/dashboard'];

/* ─── Mobile Bottom Nav items ─────────── */
const MOBILE_NAV = [
  { to: '/',                icon: Home,      label: 'Főoldal' },
  { to: '/elado-ingatlanok',icon: Building2, label: 'Ingatlanok' },
  { to: '/hot-deals',       icon: Flame,     label: 'Hot Deals' },
  { to: '/ai-transform',    icon: Bot,       label: 'AI' },
  { to: '/affiliateregister',icon: User,     label: 'Profil' },
];

export default function DarkLayout({ children, hideRightPanel = false }) {
  /* desktop: collapsed/expanded  |  mobile: drawer open/closed */
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const location = useLocation();

  const shouldHideTopBar = TOPBAR_HIDE_PATHS.some((p) => location.pathname.startsWith(p));

  /* Close mobile drawer on route change */
  React.useEffect(() => { setMobileDrawerOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">

      {/* ── LEFT SIDEBAR ─────────────────────────── */}
      <Sidebar
        /* Desktop: normal collapsed state. Mobile: open when drawer is open */
        collapsed={desktopCollapsed}
        mobileOpen={mobileDrawerOpen}
        onDesktopCollapse={(v) => setDesktopCollapsed(v)}
        onMobileClose={() => setMobileDrawerOpen(false)}
      />

      {/* ── MAIN WRAPPER ─────────────────────────── */}
      {/* lg: offset by sidebar width; mobile: full width (sidebar is overlay) */}
      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          desktopCollapsed ? 'lg:ml-16' : 'lg:ml-[240px]'
        }`}
      >
        {/* ── TOP BAR ──────────────────────────────── */}
        {!shouldHideTopBar && (
          <header className="sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur-xl border-b border-white/[0.06] flex items-center gap-2 px-3 md:px-4 py-2.5 flex-shrink-0">
            {/* Mobile hamburger */}
            <button
              className="lg:hidden flex items-center justify-center w-9 h-9 rounded-xl hover:bg-white/[0.06] text-[#9ca3af] hover:text-white transition-colors flex-shrink-0"
              onClick={() => setMobileDrawerOpen(true)}
            >
              <Menu size={20} />
            </button>

            {/* Logo (mobile only — sidebar hidden on mobile) */}
            <Link to="/" className="lg:hidden flex items-center gap-2 flex-shrink-0">
              <img src="/logo_ai.png" alt="HoppHomes" className="h-7 w-auto object-contain" />
            </Link>

            {/* Search bar */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 bg-white/[0.04] border border-white/[0.08] hover:border-[#e31937]/30 rounded-xl px-3 py-2 transition-colors group">
                <Search size={14} className="text-[#6b7280] group-hover:text-[#e31937] transition-colors flex-shrink-0" />
                <input
                  placeholder='Keresés… pl. "Teraszos lakás Budán 80M Ft-ig"'
                  className="flex-1 bg-transparent text-sm text-white placeholder-[#6b7280] outline-none min-w-0"
                />
              </div>
            </div>

            {/* Right actions */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <Link
                to="/property-upload"
                className="hidden md:flex items-center gap-2 bg-[#e31937] hover:bg-[#cc1530] text-white text-xs font-bold px-3 py-2 rounded-xl transition-colors shadow-lg shadow-red-900/20 whitespace-nowrap"
              >
                <Plus size={13} />
                Eladás indítása
              </Link>
              <Link to="/kedvencek" className="relative w-8 h-8 flex items-center justify-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] text-[#9ca3af] hover:text-white transition-colors">
                <Heart size={15} />
              </Link>
              <Link to="/ertesitesek" className="relative w-8 h-8 flex items-center justify-center rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] text-[#9ca3af] hover:text-white transition-colors">
                <Bell size={15} />
              </Link>
              <div>
                <UserAuthMenu compact />
              </div>
            </div>
          </header>
        )}

        {/* ── CONTENT + RIGHT PANEL ─────────────────── */}
        <div className="flex flex-1 min-h-0">
          {/* Main scrollable content */}
          <main className="dl-main flex-1 overflow-y-auto min-w-0 pb-16 lg:pb-0">
            {children}
          </main>
          {/* Right panel — xl+ only */}
          {!hideRightPanel && <RightPanel />}
        </div>
      </div>

      {/* ── MOBILE BOTTOM NAV ────────────────────────── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#111111]/95 backdrop-blur-xl border-t border-white/[0.06] flex items-center justify-around px-2 py-2 safe-pb">
        {MOBILE_NAV.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all ${
                active ? 'text-[#e31937]' : 'text-[#6b7280] hover:text-[#9ca3af]'
              }`}
            >
              <Icon size={20} className={active ? 'drop-shadow-[0_0_6px_rgba(227,25,55,0.6)]' : ''} />
              <span className="text-[10px] font-medium">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
